/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable no-unused-vars */
import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useSocket } from '../../context/SocketContext';
import './VideoChat.css';

const VideoChat = () => {
  const socket = useSocket();
  const localVideoRef = useRef(null);
  const remoteVideoRef = useRef(null);
  const [status, setStatus] = useState('searching');
  const [partnerId, setPartnerId] = useState(null);
  const [roomId, setRoomId] = useState(null);
  const [mediaError, setMediaError] = useState(null);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  const [unreadMessages, setUnreadMessages] = useState(0);
  const messagesEndRef = useRef(null);
  const peerRef = useRef(null);
  const localStreamRef = useRef(null);
  const userId = useRef(Date.now().toString(36) + Math.random().toString(36).substring(2));
  const isInitiatorRef = useRef(false);
  const connectionTimeoutRef = useRef(null);
  const retryCountRef = useRef(0);
  const lastPartnerIdRef = useRef(null);

  const audioContextRef = useRef(null);
  const noiseSuppressorRef = useRef(null);
  const sourceNodeRef = useRef(null);
  const destinationNodeRef = useRef(null);

  // Detect if mobile
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  useEffect(() => {
    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  useEffect(() => {
    window.scrollTo(0, 0);
    document.title = 'Video Chat - WhatsApp Style';
  }, []);

  const setupNoiseSuppression = useCallback(async () => {
    if (!audioContextRef.current || !sourceNodeRef.current) return;

    try {
      await audioContextRef.current.audioWorklet.addModule('noise-suppressor-processor.js');

      noiseSuppressorRef.current = new AudioWorkletNode(
        audioContextRef.current,
        'noise-suppressor-processor'
      );

      sourceNodeRef.current.disconnect();
      sourceNodeRef.current.connect(noiseSuppressorRef.current);
      noiseSuppressorRef.current.connect(destinationNodeRef.current);

      console.log('Noise suppression activated');
    } catch (err) {
      console.error('Could not load noise suppressor:', err);
      sourceNodeRef.current.connect(destinationNodeRef.current);
    }
  }, []);

  const initMedia = useCallback(async () => {
    setStatus('requesting_media');
    try {
      audioContextRef.current = new (window.AudioContext || window.webkitAudioContext)();

      const stream = await navigator.mediaDevices.getUserMedia({
        audio: {
          echoCancellation: true,
          noiseSuppression: true,
          autoGainControl: true,
          channelCount: 1
        },
        video: {
          facingMode: 'user',
          width: { ideal: 1280 },
          height: { ideal: 720 }
        }
      });

      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }

      if (audioContextRef.current) {
        sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(stream);
        destinationNodeRef.current = audioContextRef.current.createMediaStreamDestination();
        sourceNodeRef.current.connect(destinationNodeRef.current);

        const processedStream = new MediaStream([
          ...stream.getVideoTracks(),
          ...destinationNodeRef.current.stream.getAudioTracks()
        ]);

        localStreamRef.current = processedStream;
        await setupNoiseSuppression();
      } else {
        localStreamRef.current = stream;
      }

      if (localVideoRef.current) {
        localVideoRef.current.srcObject = localStreamRef.current;
        localVideoRef.current.play().catch(e => console.log("Video play error:", e));
      }

      setMediaError(null);
      setStatus('searching');

      if (socket) {
        socket.emit('join', userId.current);
      }
    } catch (err) {
      console.error('Media access error:', err);
      setMediaError(err.name);
      setStatus('media_error');

      try {
        const audioStream = await navigator.mediaDevices.getUserMedia({
          audio: {
            noiseSuppression: true,
            echoCancellation: true,
            autoGainControl: true
          }
        });

        if (localStreamRef.current) {
          localStreamRef.current.getTracks().forEach(track => track.stop());
        }

        if (audioContextRef.current) {
          sourceNodeRef.current = audioContextRef.current.createMediaStreamSource(audioStream);
          destinationNodeRef.current = audioContextRef.current.createMediaStreamDestination();
          sourceNodeRef.current.connect(destinationNodeRef.current);
          localStreamRef.current = destinationNodeRef.current.stream;
        } else {
          localStreamRef.current = audioStream;
        }

        setStatus('audio_only');
        setMediaError(null);

        if (socket) {
          socket.emit('join', userId.current);
        }
      } catch (audioErr) {
        console.error('Audio-only access error:', audioErr);
        setMediaError(audioErr.name);
        setStatus('media_error');
      }
    }
  }, [socket]);

  useEffect(() => {
    const heartbeat = setInterval(() => {
      if (socket && status === 'connected') {
        socket.emit('heartbeat');
      }
    }, 10000);

    return () => clearInterval(heartbeat);
  }, [socket, status]);

  useEffect(() => {
    if (window.location.protocol !== 'https:' && window.location.hostname !== 'localhost') {
      setStatus('https_required');
      return;
    }

    initMedia();

    return () => {
      if (localStreamRef.current) {
        localStreamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, [initMedia]);

  useEffect(() => {
    return () => {
      if (peerRef.current) {
        peerRef.current.close();
        peerRef.current = null;
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
      }
    };
  }, []);

  const initPeerConnection = (targetId, isInitiator) => {
    retryCountRef.current = 0;
    lastPartnerIdRef.current = targetId;

    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }

    isInitiatorRef.current = isInitiator;

    const peer = new RTCPeerConnection({
      iceServers: [
        { urls: 'stun:stun.l.google.com:19302' },
        { urls: 'stun:stun1.l.google.com:19302' },
        { urls: 'stun:stun2.l.google.com:19302' },
        { urls: 'stun:stun3.l.google.com:19302' },
        { urls: 'stun:stun4.l.google.com:19302' }
      ]
    });

    if (localStreamRef.current) {
      localStreamRef.current.getTracks().forEach(track => {
        peer.addTrack(track, localStreamRef.current);
      });
    }

    peer.ontrack = (event) => {
      console.log('Received remote stream', event.streams);
      if (event.streams && event.streams.length > 0) {
        const videoStream = event.streams.find(stream =>
          stream.getVideoTracks().length > 0 || stream.getAudioTracks().length > 0
        );

        if (videoStream && remoteVideoRef.current) {
          console.log('Setting remote video source');
          remoteVideoRef.current.srcObject = videoStream;

          remoteVideoRef.current.play().catch(e =>
            console.error("Remote video play error:", e)
          );
        }
      }
    };

    peer.onicecandidate = (event) => {
      if (event.candidate && socket && targetId) {
        console.log('Sending ICE candidate');
        socket.emit('signal', {
          to: targetId,
          type: 'candidate',
          candidate: event.candidate
        });
      }
    };

    peer.oniceconnectionstatechange = () => {
      console.log(`ICE state: ${peer.iceConnectionState}`);
      if (peer.iceConnectionState === 'connected') {
        setStatus('connected');
      }
      else if (peer.iceConnectionState === 'failed' ||
        peer.iceConnectionState === 'disconnected') {
        setStatus('connection_error');
      }
    };

    peer.onconnectionstatechange = () => {
      console.log(`Connection state: ${peer.connectionState}`);
      if (peer.connectionState === 'connected') {
        setStatus('connected');
        if (connectionTimeoutRef.current) {
          clearTimeout(connectionTimeoutRef.current);
          connectionTimeoutRef.current = null;
        }
      }
      else if (peer.connectionState === 'disconnected' ||
        peer.connectionState === 'failed') {
        setStatus('connection_error');
      }
    };

    if (isInitiator) {
      console.log('Creating offer as initiator');
      peer.createOffer({
        offerToReceiveAudio: true,
        offerToReceiveVideo: true
      })
        .then(offer => {
          console.log('Offer created');
          return peer.setLocalDescription(offer);
        })
        .then(() => {
          console.log('Sending offer');
          socket.emit('signal', {
            to: targetId,
            type: 'offer',
            offer: peer.localDescription
          });

          connectionTimeoutRef.current = setTimeout(() => {
            if (peer.connectionState !== 'connected') {
              console.log('Connection timed out');
              setStatus('connection_timeout');
              peer.close();
            }
          }, 15000);
        })
        .catch(err => {
          console.error('Error creating offer:', err);
          setStatus('connection_error');
        });
    }

    peerRef.current = peer;
  };

  useEffect(() => {
    if (!socket) return;

    const handlePaired = ({ roomId, partnerId, isInitiator }) => {
      console.log(`Paired with partner: ${partnerId}, Initiator: ${isInitiator}`);
      setRoomId(roomId);
      setPartnerId(partnerId);
      setStatus('connecting');
      initPeerConnection(partnerId, isInitiator);
      setMessages([]);
      setUnreadMessages(0);
      // Auto-open chat on desktop when connected
      if (window.innerWidth >= 768) {
        setIsChatOpen(true);
      }
    };

    const handlePartnerDisconnected = () => {
      console.log('Partner disconnected');
      setStatus('partner_left');
      setPartnerId(null);
      if (remoteVideoRef.current) {
        remoteVideoRef.current.srcObject = null;
      }
      if (peerRef.current) {
        peerRef.current.close();
        peerRef.current = null;
      }
      if (connectionTimeoutRef.current) {
        clearTimeout(connectionTimeoutRef.current);
        connectionTimeoutRef.current = null;
      }

      setMessages(prev => [...prev, {
        text: 'Partner has disconnected',
        sender: 'system',
        timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
      }]);
    };

    const handleSignal = (data) => {
      console.log(`Received signal of type: ${data.type} from ${data.from}`);
      if (!peerRef.current || !data.type) return;

      switch (data.type) {
        case 'offer':
          console.log('Processing offer');
          peerRef.current.setRemoteDescription(new RTCSessionDescription(data.offer))
            .then(() => {
              return peerRef.current.createAnswer({
                offerToReceiveAudio: true,
                offerToReceiveVideo: true
              });
            })
            .then(answer => {
              return peerRef.current.setLocalDescription(answer);
            })
            .then(() => {
              console.log('Sending answer');
              socket.emit('signal', {
                to: data.from,
                type: 'answer',
                answer: peerRef.current.localDescription
              });

              connectionTimeoutRef.current = setTimeout(() => {
                if (peerRef.current.connectionState !== 'connected') {
                  console.log('Connection timed out');
                  setStatus('connection_timeout');
                  peerRef.current.close();
                }
              }, 15000);
            })
            .catch(err => {
              console.error('Error handling offer:', err);
              setStatus('connection_error');
            });
          break;

        case 'answer':
          console.log('Processing answer');
          peerRef.current.setRemoteDescription(new RTCSessionDescription(data.answer))
            .then(() => {
              console.log('Remote description set successfully');
            })
            .catch(err => {
              console.error('Error setting answer:', err);
              setStatus('connection_error');
            });
          break;

        case 'candidate':
          console.log('Processing ICE candidate');
          peerRef.current.addIceCandidate(new RTCIceCandidate(data.candidate))
            .catch(err => {
              console.error('Error adding ICE candidate:', err);
            });
          break;

        case 'chat':
          console.log('Received chat message:', data.message);
          const newMessage = {
            text: data.message,
            sender: 'partner',
            timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
          };
          setMessages(prev => [...prev, newMessage]);
          if (!isChatOpen && isMobile) {
            setUnreadMessages(prev => prev + 1);
          }
          break;

        default:
          console.warn('Unknown signal type:', data.type);
      }
    };

    socket.on('paired', handlePaired);
    socket.on('partner_disconnected', handlePartnerDisconnected);
    socket.on('signal', handleSignal);

    socket.on('connect', () => {
      console.log('Socket connected');
      if (status === 'disconnected') {
        setStatus('searching');
        if (localStreamRef.current) {
          socket.emit('join', userId.current);
        }
      }
    });

    socket.on('disconnect', () => {
      console.log('Socket disconnected');
      setStatus('disconnected');
      fullCleanup();
    });

    socket.on('connect_error', (err) => {
      console.error('Socket connection error:', err);
      setStatus('connection_error');
    });

    return () => {
      socket.off('paired', handlePaired);
      socket.off('partner_disconnected', handlePartnerDisconnected);
      socket.off('signal', handleSignal);
      socket.off('connect');
      socket.off('disconnect');
      socket.off('connect_error');
    };
  }, [socket, isChatOpen, isMobile]);

  const fullCleanup = useCallback(() => {
    console.log('Performing full cleanup');

    if (peerRef.current) {
      peerRef.current.close();
      peerRef.current = null;
    }

    if (remoteVideoRef.current) {
      remoteVideoRef.current.srcObject = null;
    }

    if (connectionTimeoutRef.current) {
      clearTimeout(connectionTimeoutRef.current);
      connectionTimeoutRef.current = null;
    }

    if (sourceNodeRef.current) {
      sourceNodeRef.current.disconnect();
      sourceNodeRef.current = null;
    }

    if (destinationNodeRef.current) {
      destinationNodeRef.current.disconnect();
      destinationNodeRef.current = null;
    }

    if (noiseSuppressorRef.current) {
      noiseSuppressorRef.current.disconnect();
      noiseSuppressorRef.current = null;
    }

    if (audioContextRef.current && audioContextRef.current.state !== 'closed') {
      audioContextRef.current.close();
      audioContextRef.current = null;
    }

    setPartnerId(null);
    setRoomId(null);
    setMessages([]);
    setStatus('searching');
    if (isMobile) {
      setIsChatOpen(false);
    }
    setUnreadMessages(0);
  }, [isMobile]);

  const handleNextPartner = () => {
    if (socket) {
      console.log('Requesting next partner');
      socket.emit('next');
      fullCleanup();
    }
  };

  const handleRetryMedia = useCallback(async () => {
    setStatus('retrying');
    fullCleanup();
    try {
      await initMedia();
    } catch (err) {
      setStatus('media_error');
    }
  }, [initMedia, fullCleanup]);

  const handleRefresh = () => {
    console.log('Refreshing connection');
    fullCleanup();
    if (socket) {
      socket.emit('join', userId.current);
    }
  };

  const sendMessage = () => {
    if (!newMessage.trim() || !partnerId || !socket) {
      console.warn('Cannot send - missing requirements:', {
        hasMessage: !!newMessage.trim(),
        hasPartner: !!partnerId,
        hasSocket: !!socket
      });
      return;
    }

    console.log('Sending message to:', partnerId);
    socket.emit('chat_message', {
      to: partnerId,
      message: newMessage.trim()
    });

    const newMsg = {
      text: newMessage.trim(),
      sender: 'me',
      timestamp: new Date().toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
    };
    setMessages(prev => [...prev, newMsg]);
    setNewMessage('');
  };

  useEffect(() => {
    const handleIncomingMessage = (data) => {
      if (data.from !== partnerId) return;

      const newMessage = {
        text: data.message,
        sender: 'partner',
        timestamp: new Date().toLocaleTimeString()
      };
      setMessages(prev => [...prev, newMessage]);
      if (!isChatOpen && isMobile) {
        setUnreadMessages(prev => prev + 1);
      }
    };

    socket.on('chat_message', handleIncomingMessage);
    return () => socket.off('chat_message', handleIncomingMessage);
  }, [partnerId, isChatOpen, isMobile]);

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const getStatusMessage = () => {
    const statusIcons = {
      disconnected: '🔴',
      searching: '🔍',
      connecting: '🔄',
      connected: '✅',
      partner_left: '⚠️',
      media_error: '❌',
      audio_only: '🎤',
      connection_error: '⚠️',
      retrying: '🔄',
      requesting_media: '📷',
      https_required: '🔒',
      connection_timeout: '⏰'
    };

    const messages = {
      disconnected: 'Disconnected',
      searching: 'Searching for partner...',
      connecting: 'Connecting...',
      connected: 'Connected',
      partner_left: 'Partner left',
      media_error: `Error: ${mediaError || 'Camera access denied'}`,
      audio_only: 'Audio only',
      connection_error: 'Connection error',
      retrying: 'Retrying...',
      requesting_media: 'Requesting access...',
      https_required: 'HTTPS required',
      connection_timeout: 'Connection timeout'
    };

    const icon = statusIcons[status] || '❓';
    const message = messages[status] || status;

    return (
      <>
        <span className="vc-status-icon">{icon}</span>
        <span>{message}</span>
      </>
    );
  };

  useEffect(() => {
    if (status === 'connection_error' || status === 'connection_timeout') {
      if (retryCountRef.current < 3 && partnerId) {
        const delay = Math.pow(2, retryCountRef.current) * 1000;
        const timer = setTimeout(() => {
          initPeerConnection(partnerId, isInitiatorRef.current);
          retryCountRef.current++;
        }, delay);
        return () => clearTimeout(timer);
      } else {
        handleNextPartner();
      }
    }
  }, [status, partnerId]);

  const toggleChat = () => {
    setIsChatOpen(!isChatOpen);
    if (!isChatOpen && unreadMessages > 0) {
      setUnreadMessages(0);
    }
  };

  // Auto-open chat on desktop
  useEffect(() => {
    if (!isMobile && status === 'connected' && !isChatOpen) {
      setIsChatOpen(true);
    }
  }, [isMobile, status, isChatOpen]);

  return (
    <div className="vc-container">
      <div className="vc-status-bar" data-status={status}>
        {getStatusMessage()}
      </div>

      <div className="vc-main-content">
        <div className="vc-video-section">
          <div className="vc-video-grid">
            {/* Remote Video - Full Screen */}
            <div className="vc-remote-container">
              <video
                ref={remoteVideoRef}
                autoPlay
                playsInline
                className="vc-remote-video"
              />
              <div className="vc-video-label">Partner</div>
              
              {/* Network Indicator */}
              {status === 'connected' && (
                <div className="vc-network-indicator">
                  <div className="vc-network-bars">
                    <div className="vc-network-bar"></div>
                    <div className="vc-network-bar"></div>
                    <div className="vc-network-bar"></div>
                    <div className="vc-network-bar"></div>
                  </div>
                  <span>Connected</span>
                </div>
              )}
            </div>

            {/* Local Video - Small Corner */}
            <div className="vc-local-container">
              <video
                ref={localVideoRef}
                autoPlay
                muted
                playsInline
                className={`vc-local-video ${status === 'audio_only' ? 'vc-audio-only' : ''}`}
              />
              <div className="vc-video-label">You</div>
            </div>
          </div>

          {/* Chat Toggle Button - Mobile Only */}
          {isMobile && (
            <button
              onClick={toggleChat}
              className="vc-control-btn vc-chat-toggle-btn"
              disabled={status !== 'connected'}
            >
              <span className="vc-btn-icon">💬</span>
              {unreadMessages > 0 && (
                <div className="vc-chat-badge">{unreadMessages}</div>
              )}
            </button>
          )}

          {/* Main Controls */}
          <div className="vc-controls">
            <button
              onClick={handleNextPartner}
              disabled={status !== 'connected' && status !== 'partner_left' && status !== 'connection_error' && status !== 'connection_timeout'}
              className="vc-control-btn vc-next-btn"
            >
              <span className="vc-btn-icon">⟳</span>
            </button>

            {(status === 'disconnected' || status === 'partner_left' || status === 'connection_error') && (
              <button
                onClick={handleRefresh}
                className="vc-control-btn vc-refresh-btn"
              >
                <span className="vc-btn-icon">↻</span>
              </button>
            )}

            {(status === 'media_error' || status === 'https_required') && (
              <button
                onClick={handleRetryMedia}
                className="vc-control-btn vc-retry-btn"
              >
                <span className="vc-btn-icon">📷</span>
              </button>
            )}
          </div>
        </div>

        {/* Chat Section */}
        <div className={`vc-chat-section ${isChatOpen ? 'vc-chat-open' : ''}`}>
          {isMobile && <div className="vc-chat-handle" onClick={toggleChat}></div>}
          <div className="vc-chat-panel">
            <div className="vc-chat-header">
              <h3>Chat</h3>
              <div className={`vc-chat-status ${status === 'connected' ? '' : 'vc-status-disconnected'}`}>
                <div className="vc-status-indicator"></div>
                <span>{status === 'connected' ? 'Connected' : 'Disconnected'}</span>
              </div>
            </div>

            <div className="vc-messages-container">
              {messages.length === 0 ? (
                <div className="vc-no-messages">
                  <p>No messages yet</p>
                  <p>Say hello to your partner!</p>
                </div>
              ) : (
                messages.map((msg, index) => (
                  <div
                    key={index}
                    className={`vc-message ${msg.sender === 'me' ? 'vc-my-message' :
                      msg.sender === 'partner' ? 'vc-partner-message' : 'vc-system-message'}`}
                  >
                    <div className="vc-message-content">
                      <div className="vc-message-text">{msg.text}</div>
                      <div className="vc-message-time">{msg.timestamp}</div>
                    </div>
                  </div>
                ))
              )}
              <div ref={messagesEndRef} />
            </div>

            <div className="vc-chat-input">
              <textarea
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Type a message..."
                disabled={status !== 'connected'}
                rows={1}
              />
              <button
                onClick={sendMessage}
                disabled={newMessage.trim() === '' || status !== 'connected'}
                className="vc-send-btn"
              />
            </div>
          </div>
        </div>
      </div>

      {/* Help Section */}
      {(status === 'media_error' || status === 'https_required') && (
        <div className="vc-help-section">
          <h3 className="vc-help-title">Camera Access Required</h3>
          <p className="vc-help-text">To use this app, you need to allow camera access:</p>
          <ul className="vc-help-list">
            <li className="vc-help-item">Click the camera icon in your browser's address bar</li>
            <li className="vc-help-item">Select "Always allow" for camera and microphone</li>
            <li className="vc-help-item">Refresh the page after granting permissions</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default VideoChat;